import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  HostListener,
  OnInit,
  computed,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FilterPanelComponent } from '../../shared/components/filter-panel/filter-panel.component';
import { ProductCardComponent } from '../../shared/components/product-card/product-card.component';
import { InquiryModalComponent } from '../../shared/components/inquiry-modal/inquiry-modal.component';
import { ListToolbarComponent } from '../../shared/components/list-toolbar/list-toolbar.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { PublicListingService } from '../../core/services/listing/public-listing.service';
import { AppMainService } from '../../core/services/core/app-main.service';
import { AppMaster } from '../../core/models/api/app-main.model';
import { FilterConfig, FilterState } from '../../shared/models/filter.model';

@Component({
  selector: 'app-yachts',
  standalone: true,
  imports: [
    CommonModule,
    FilterPanelComponent,
    ProductCardComponent,
    InquiryModalComponent,
    ListToolbarComponent,
    PaginationComponent,
  ],
  templateUrl: './yachts.component.html',
  styleUrls: ['./yachts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YachtsComponent implements OnInit {
  private productService = inject(PublicListingService);
  private appMainService = inject(AppMainService);
  private router = inject(Router);

  private platformId = inject(PLATFORM_ID);

  readonly pageSize = 10;
  readonly initialPage = 1;

  // Signals for View State
  viewMode = signal<'grid' | 'list'>(this.getInitialViewMode());
  currentSort = signal<string>(this.getInitialSort());
  currentPage = signal<number>(this.getInitialPage());

  private getInitialViewMode(): 'grid' | 'list' {
    if (isPlatformBrowser(this.platformId)) {
      return (localStorage.getItem('seger_yachts_view') as any) || 'grid';
    }
    return 'grid';
  }

  private getInitialSort(): string {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('seger_yachts_sort') || 'newest';
    }
    return 'newest';
  }

  private getInitialPage(): number {
    if (isPlatformBrowser(this.platformId)) {
      return parseInt(localStorage.getItem('seger_yachts_page') || '1', 10);
    }
    return 1;
  }

  // Mobile Filter Drawer State
  // Mobile Filter Drawer State
  isFilterDrawerOpen = signal<boolean>(false);

  // Initial Loading State
  isLoading = signal<boolean>(false);

  // Filter Configuration
  filterConfig = signal<FilterConfig[]>([]);

  priceConfig = { min: 0, max: 1000000 };
  yearConfig = { min: 1900, max: new Date().getFullYear() };

  activeFilters = signal<FilterState>(this.loadFiltersFromStorage());

  private loadFiltersFromStorage(): FilterState {
    const defaultState = {
      price: { min: this.priceConfig.min, max: this.priceConfig.max },
      year: { min: this.yearConfig.min, max: this.yearConfig.max },
    };
    try {
      if (isPlatformBrowser(this.platformId)) {
        const saved = localStorage.getItem('seger_yachts_filters');
        if (saved) {
          return { ...defaultState, ...JSON.parse(saved) };
        }
      }
    } catch {}
    return defaultState;
  }

  // Global Modal State
  isInterestModalOpen = signal<boolean>(false);
  selectedProductId = signal<number | null>(null);
  selectedBrokerName = signal<string>('the broker');

  // Fetch all yachts once
  private allYachtsRaw = toSignal(this.productService.getCachedYachts(), { initialValue: [] });

  // Computed: Dynamic Listing Title based on active filters or first product
  listingTitle = computed(() => {
    const filters = this.activeFilters();
    const typeFilter = filters['yachtType'];

    // 1. Check if a specific type is selected in filters
    if (Array.isArray(typeFilter) && typeFilter.length === 1) {
      const typeId = typeFilter[0];
      const configSection = this.filterConfig().find((c) => c.key === 'yachtType');
      const option = configSection?.options?.find((opt) => opt.value === typeId);
      if (option) return `${option.label} Boats`;
    }

    // 2. Fallback: If no filter but we have products, use the first product's type
    const firstProduct = this.products()[0];
    if (firstProduct?.boatTypeName) {
      return `${firstProduct.boatTypeName} Boats`;
    }

    return 'Yachts';
  });

  // Computed: Filtered + Sorted + Paginated list
  private filteredAndSorted = computed(() => {
    let items = [...this.allYachtsRaw()];
    const filters = this.activeFilters();
    const sort = this.currentSort();

    // Apply filters
    if (filters['price']) {
      const price = filters['price'] as { min: number; max: number };
      items = items.filter(
        (b: any) => b.price >= (price.min || 0) && b.price <= (price.max || Infinity),
      );
    }
    if (filters['year']) {
      const year = filters['year'] as { min: number; max: number };
      items = items.filter((b: any) => {
        const y = b.boat?.boatYear ?? b.boatYear ?? null;
        if (y === null || y === 0) return true;
        return y >= year.min && y <= year.max;
      });
    }
    if (filters['condition']) {
      const conds = Array.isArray(filters['condition'])
        ? filters['condition']
        : [filters['condition']];
      if (conds.length > 0) {
        items = items.filter((b: any) => {
          const itemCond = (b.boat?.condition || b.condition || '').toLowerCase();
          return conds.some((c: string) => itemCond === c.toLowerCase());
        });
      }
    }
    if (filters['location'] && filters['location'] !== 'all') {
      const locId = Array.isArray(filters['location'])
        ? filters['location'][0]
        : filters['location'];
      items = items.filter((b: any) =>
        (b.productLocations || []).some((l: any) => l.locationId === +locId),
      );
    }
    if (filters['yachtType']) {
      const types = Array.isArray(filters['yachtType'])
        ? filters['yachtType']
        : [filters['yachtType']];
      if (types.length > 0) {
        items = items.filter((b: any) => {
          const typeId = b.boat?.boatTypeId ?? b.boatTypeId ?? null;
          return typeId && types.some((t: any) => +typeId === +t);
        });
      }
    }
    if (filters['rating']) {
      const minRating = filters['rating'] as number;
      if (minRating > 0) {
        items = items.filter((b: any) => {
          const reviews = b.productReviews || [];
          const avg =
            reviews.length > 0
              ? reviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0) / reviews.length
              : 0;
          return avg >= minRating;
        });
      }
    }

    // Apply sort
    const itemsToSort = [...items];
    const getPrice = (item: any) => item.price ?? item.boat?.price ?? item.product?.price ?? 0;
    const getDate = (item: any) => {
      const d = item.updatedDate || item.createdDate || item.boat?.updatedDate || '';
      return d ? new Date(d).getTime() : 0;
    };

    itemsToSort.sort((a: any, b: any) => {
      if (sort === 'price_asc') return getPrice(a) - getPrice(b);
      if (sort === 'price_desc') return getPrice(b) - getPrice(a);
      if (sort === 'newest') return getDate(b) - getDate(a);

      // Default: Newest by ID
      return (b.productId || 0) - (a.productId || 0);
    });

    return itemsToSort;
  });

  // Final Derived Signals for Template
  totalItems = computed(() => this.filteredAndSorted().length);

  products = computed(() => {
    const items = this.filteredAndSorted();
    const start = (this.currentPage() - 1) * this.pageSize;
    const paginated = items.slice(start, start + this.pageSize);

    const mapped = paginated.map((dto: any) => {
      const result = (this.productService as any).toProductDetail(dto);
      if (!result) {
        console.error(
          `[YachtsComponent] CRITICAL: Mapping returned null for Product ID ${dto.productId}`,
          dto,
        );
      }
      return result;
    });

    console.log('[YachtsComponent] Render Pipeline Counts:', {
      totalRawItems: this.allYachtsRaw().length,
      totalFiltered: items.length,
      pageItems: mapped.length,
    });

    return mapped;
  });

  openInterestModal(product: any) {
    this.selectedProductId.set(product.id || product.productId);
    this.selectedBrokerName.set(product.brokerName || 'the broker');
    this.isInterestModalOpen.set(true);
  }

  ngOnInit() {
    this.loadMasterData();
  }

  private loadMasterData() {
    this.appMainService.getMasterConfig().subscribe({
      next: (master: AppMaster) => {
        this.initializeFilters(master);
      },
    });
  }

  private initializeFilters(master: any) {
    const locations = (master.appMain?.brokerLocations || []).map((l: any) => ({
      label: l.locationName,
      value: l.locationId.toString(),
    }));

    const boatTypes = (master.boatTypes || []).map((t: any) => ({
      label: t.boatTypeName,
      value: t.boatTypeId.toString(),
    }));

    this.filterConfig.set([
      {
        key: 'location',
        label: 'Location',
        type: 'radio',
        options: [...locations, { label: 'All', value: 'all' }],
        collapsed: false,
      },
      {
        key: 'yachtType',
        label: 'Yacht Type',
        type: 'checkbox',
        options: boatTypes,
        collapsed: false,
      },
      {
        key: 'condition',
        label: 'Condition',
        type: 'checkbox',
        options: [
          { label: 'Mint', value: 'Mint' },
          { label: 'Great Shape', value: 'Great Shape' },
          { label: 'Average', value: 'Average' },
          { label: 'Project Boat', value: 'Project Boat' },
        ],
        collapsed: false,
      },
      {
        key: 'year',
        label: 'Year',
        type: 'range',
        min: this.yearConfig.min,
        max: this.yearConfig.max,
        collapsed: false,
      },
      {
        key: 'price',
        label: 'Price Range',
        type: 'range',
        min: this.priceConfig.min,
        max: this.priceConfig.max,
        step: 1000,
        collapsed: false,
        linear: false, // Enable non-linear mapping for 0-20M range
      },
      {
        key: 'rating',
        label: 'Rating',
        type: 'rating',
        options: [], // Not needed for custom stars component
        collapsed: false,
      },
    ]);
  }

  // Constructor with effect to sync URL → Component state AND load data
  constructor() {
    // Smart filtering is now handled via computed signals above.
  }

  // Event Handlers
  onFilterChange(filters: FilterState) {
    this.activeFilters.set(filters);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_yachts_filters', JSON.stringify(filters));
      localStorage.setItem('seger_yachts_page', '1');
    }
  }

  onSortChange(sort: string) {
    this.currentSort.set(sort);
    this.currentPage.set(1);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_yachts_sort', sort);
      localStorage.setItem('seger_yachts_page', '1');
    }
  }

  onViewChange(mode: 'grid' | 'list') {
    this.viewMode.set(mode);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_yachts_view', mode);
    }
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_yachts_page', page.toString());
    }
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onSourceChange(source: 'yachts' | 'merchandises') {
    if (source === 'merchandises') {
      this.router.navigate(['/merchandises']);
    }
  }

  // Mobile Filter Drawer Handlers
  onFilterToggle() {
    this.isFilterDrawerOpen.set(!this.isFilterDrawerOpen());
    this.toggleBodyScroll();
  }

  closeFilterDrawer() {
    this.isFilterDrawerOpen.set(false);
    this.enableBodyScroll();
  }

  onDrawerBackdropClick() {
    this.closeFilterDrawer();
  }

  // Clear All Filters
  clearAllFilters() {
    const defaultState = {
      price: { min: this.priceConfig.min, max: this.priceConfig.max },
      year: { min: this.yearConfig.min, max: this.yearConfig.max },
    };
    this.activeFilters.set(defaultState);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('seger_yachts_filters', JSON.stringify(defaultState));
    }
    this.currentPage.set(1);
    this.closeFilterDrawer();
  }

  // ESC Key Handler
  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isFilterDrawerOpen()) {
      this.closeFilterDrawer();
    }
  }

  // Body Scroll Lock Management (Disabled per requirement)
  private toggleBodyScroll() {
    // if (this.isFilterDrawerOpen()) {
    //     document.body.style.overflow = 'hidden';
    // } else {
    //     document.body.style.overflow = '';
    // }
  }

  private enableBodyScroll() {
    // document.body.style.overflow = '';
  }
}
